import {
    AppBar,
    Box,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    makeStyles,
    Menu,
    MenuItem,
    Snackbar,
    Toolbar,
    Tooltip,
    Typography,
    withStyles,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import clsx from 'clsx';
import { useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import logout from '../actions/logout';
import { ReactComponent as Logo } from '../assets/logo.svg';
import AboutModal from '../components/AboutModal';
import AddDefaultAvatarDialog from '../components/admin/AddDefaultAvatarDialog';
import AddTermsDialog from '../components/admin/AddTermsDialog';
import MainMenuItems from '../components/MainMenuItems';
import Page from '../components/Page';
import ErrorCodes from '../consts/ErrorCodes';
import Permissions from '../consts/Permissions';
import getPhoneCode from '../helpers/getPhoneCode';
import isPermissionGranted from '../helpers/isPermissionGranted';
import { ReactComponent as ArrowIcon } from '../icons/arrow.svg';
import { ReactComponent as HamburgerIcon } from '../icons/hamburger.svg';
import useStore from '../store/store';
import AdminTestsPage from './admin/KitsPage';
import AdminUserPage from './admin/UserPage';
import ContactUsPage from './ContactUsPage';
import DashboardPage from './DashboardPage';
import EventsPage from './EventsPage';
import MemberPage from './MemberPage';
import MembersPage from './MembersPage';
import TestMenuPage from './TestMenuPage';

const CustomTooltip = withStyles((theme) => ({
    tooltip: {
        display: 'inline-flex',
        padding: '6px 10px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        borderRadius: '8px',
        background: theme.palette.primary.main,
        boxShadow: `2px 2px 4px 0px rgba(0, 0, 0, 0.19)`,
        color: theme.palette.primary.background,
        fontFamily: 'Noto Sans',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '150%',
    },
}))(Tooltip);

export default function MainPage() {
    const title = useStore((state) => state.title);
    const formError = useStore((state) => state.formError);
    const successMessage = useStore((state) => state.successMessage);
    const isInitialized = useStore((state) => state.isInitialized);
    const siteName = 'Lifespan Genome Portal';
    const [snackbarOpen, setSnackbarOpen] = useState(true);
    const iso2 = localStorage.getItem('iso2');
    const phone =
        useStore((state) => state.phone) || localStorage.getItem('phone');
    const classes = useStyles();
    const [drawerOpen, setDrawerOpen] = useState(
        window.screen.width >= 1280 ? true : false
    );
    const [anchor, setAnchor] = useState();
    const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
    const [addTermsFormOpen, setAddTermsFormOpen] = useState(false);
    const [addDefaultAvatarFormOpen, setAddDefaultAvatarFormOpen] =
        useState(false);

    const handleDrawerOpen = () => {
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const handleClick = (event) => {
        setAnchor(event.currentTarget);
    };

    const handleClose = () => {
        setAnchor(null);
    };

    const handleAboutClick = () => {
        setAboutDialogOpen(true);
        setAnchor(null);
    };

    const handleAddTermsClick = () => {
        setAddTermsFormOpen(true);
        setAnchor(null);
    };

    const handleAddDefaultAvatarClick = () => {
        setAddDefaultAvatarFormOpen(true);
        setAnchor(null);
    };

    const handleAboutDialogCancel = () => {
        setAboutDialogOpen(false);
    };

    const Alert = (props) => {
        return <MuiAlert elevation={6} variant="filled" {...props} />;
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const setErrorMessage = (errCode) => {
        switch (errCode) {
            case ErrorCodes.access_denied:
                return 'Access denied!';
            case ErrorCodes.already_assigned:
                return 'Already assigned!';
            case ErrorCodes.already_confirmed:
                return 'Already confirmed!';
            case ErrorCodes.auth_failed:
                return 'Authorization failed!';
            case ErrorCodes.internal_server_error:
                return 'Internal server error!';
            case ErrorCodes.invalid_param:
                return 'Invalid parameter!';
            case ErrorCodes.invalid_token:
                return 'Invalid token!';
            case ErrorCodes.phone_exists:
                return 'Phone already exists!';
            case ErrorCodes.phone_not_found:
                return 'Phone not found!';
            case ErrorCodes.too_many_attempts:
                return 'Too many attempts!';
            case ErrorCodes.rpc_communication_error:
                return 'Error during communication with the server via WebSocket!';
            case ErrorCodes.other:
                return 'Something went wrong (error code 15)!';
            case ErrorCodes.failed_to_create_websocket:
                return 'Failed to create WebSocket instance!';
            case ErrorCodes.err_network:
                return 'Network error (error code 17)!';
            case ErrorCodes.email_not_found:
                return 'E-mail not found!';
            case ErrorCodes.duplicate:
                return 'Duplicate! Please select another value.';
            default:
                return 'Something went wrong!';
        }
    };

    return (
        isInitialized && (
            <>
                <div className={classes.root}>
                    <CssBaseline />
                    <AppBar
                        className={clsx(classes.appBar, drawerOpen)}
                        position="fixed"
                    >
                        <Toolbar className={classes.toolbar}>
                            <Logo className={classes.logo} />
                            <Typography
                                component="h1"
                                variant="h6"
                                color="inherit"
                                noWrap
                                className={classes.title}
                            >
                                {title}
                            </Typography>
                            <IconButton
                                aria-label="more"
                                aria-controls="long-menu"
                                aria-haspopup="true"
                                onClick={handleClick}
                                className={classes.accountIcon}
                            >
                                <AccountCircleIcon fontSize="large" />
                            </IconButton>
                            <Menu
                                id="menu"
                                anchorEl={anchor}
                                keepMounted
                                open={Boolean(anchor)}
                                onClose={handleClose}
                            >
                                <MenuItem>
                                    Signed in as&nbsp;
                                    <b>
                                        +{getPhoneCode(iso2)} {phone}
                                    </b>
                                </MenuItem>
                                {isPermissionGranted(
                                    Permissions.admin_screen
                                ) && (
                                    <div>
                                        <Divider />
                                        <MenuItem onClick={handleAddTermsClick}>
                                            Add terms & conditions
                                        </MenuItem>
                                        <MenuItem
                                            onClick={
                                                handleAddDefaultAvatarClick
                                            }
                                        >
                                            Add default avatars
                                        </MenuItem>
                                    </div>
                                )}
                                <Divider />
                                <MenuItem onClick={logout}>Sign out</MenuItem>
                                <Divider />
                                <MenuItem onClick={handleAboutClick}>
                                    About
                                </MenuItem>
                            </Menu>
                        </Toolbar>
                    </AppBar>
                    <Drawer
                        variant="permanent"
                        classes={{
                            paper: clsx(
                                classes.drawerPaper,
                                !drawerOpen && classes.drawerPaperClose
                            ),
                        }}
                        open={drawerOpen}
                    >
                        <Divider />
                        <List>
                            <div className={classes.toolbarIcon}></div>
                            {!drawerOpen && (
                                <Box display="flex" justifyContent="center">
                                    <IconButton
                                        edge="start"
                                        aria-label="open drawer"
                                        onClick={handleDrawerOpen}
                                    >
                                        <CustomTooltip
                                            title="Expand Menu"
                                            placement="right"
                                        >
                                            <HamburgerIcon
                                                className={classes.icon}
                                            />
                                        </CustomTooltip>
                                    </IconButton>
                                </Box>
                            )}
                            {drawerOpen && (
                                <Box display="flex" justifyContent="flex-end">
                                    <IconButton onClick={handleDrawerClose}>
                                        <ArrowIcon className={classes.icon} />
                                    </IconButton>
                                </Box>
                            )}
                            <MainMenuItems
                                activePage={title}
                                drawerOpen={drawerOpen}
                            />
                        </List>
                    </Drawer>
                    <main className={classes.content}>
                        <div className={classes.appBarSpacer} />
                        <div>
                            <Routes>
                                {isPermissionGranted(
                                    Permissions.genome_portal
                                ) && (
                                    <>
                                        <Route
                                            path="/*"
                                            element={
                                                <Page
                                                    title={`${siteName} - Dashboard`}
                                                >
                                                    <DashboardPage />
                                                </Page>
                                            }
                                        />
                                        <Route
                                            path="/members"
                                            element={
                                                <Page
                                                    title={`${siteName} - Members`}
                                                >
                                                    <MembersPage />
                                                </Page>
                                            }
                                        />
                                        <Route
                                            path="/member/:memberId"
                                            element={
                                                <Page
                                                    title={`${siteName} - Member`}
                                                >
                                                    <MemberPage />
                                                </Page>
                                            }
                                        />
                                        <Route
                                            path="/menu"
                                            element={
                                                <Page
                                                    title={`${siteName} - Test Menu`}
                                                >
                                                    <TestMenuPage />
                                                </Page>
                                            }
                                        />
                                        <Route
                                            path="/contact"
                                            element={
                                                <Page
                                                    title={`${siteName} - Contact Us`}
                                                >
                                                    <ContactUsPage />
                                                </Page>
                                            }
                                        />
                                        <Route
                                            path="/events"
                                            element={
                                                <Page
                                                    title={`${siteName} - News & Events`}
                                                >
                                                    <EventsPage />
                                                </Page>
                                            }
                                        />
                                    </>
                                )}
                                {isPermissionGranted(
                                    Permissions.admin_screen
                                ) && (
                                    <>
                                        <Route
                                            path="/*"
                                            element={
                                                <Page
                                                    title={`${siteName} - Admin - Users`}
                                                >
                                                    <AdminUserPage />
                                                </Page>
                                            }
                                        />
                                        <Route
                                            path="/admin"
                                            element={
                                                <Page
                                                    title={`${siteName} - Admin - Users`}
                                                >
                                                    <AdminUserPage />
                                                </Page>
                                            }
                                        />
                                        <Route
                                            path="/admin/tests"
                                            element={
                                                <Page
                                                    title={`${siteName} - Admin - Tests`}
                                                >
                                                    <AdminTestsPage />
                                                </Page>
                                            }
                                        />
                                        <Route
                                            path="/admin/user/:userId"
                                            element={
                                                <Page
                                                    title={`${siteName} - User`}
                                                >
                                                    <AdminUserPage />
                                                </Page>
                                            }
                                        />
                                        <Route
                                            path="/member/:memberId"
                                            element={
                                                <Page
                                                    title={`${siteName} - Member`}
                                                >
                                                    <MemberPage />
                                                </Page>
                                            }
                                        />
                                        <Route
                                            path="/contact"
                                            element={
                                                <Page
                                                    title={`${siteName} - Contact Us`}
                                                >
                                                    <ContactUsPage />
                                                </Page>
                                            }
                                        />
                                    </>
                                )}
                            </Routes>
                        </div>

                        {formError && (
                            <Snackbar
                                open={snackbarOpen}
                                autoHideDuration={6000}
                                onClose={handleSnackbarClose}
                            >
                                <Alert
                                    onClose={handleSnackbarClose}
                                    severity="error"
                                >
                                    {setErrorMessage(formError)}
                                </Alert>
                            </Snackbar>
                        )}

                        {successMessage && (
                            <Snackbar
                                open={snackbarOpen}
                                autoHideDuration={6000}
                                onClose={handleSnackbarClose}
                            >
                                <Alert
                                    onClose={handleSnackbarClose}
                                    severity="success"
                                >
                                    {successMessage}
                                </Alert>
                            </Snackbar>
                        )}
                    </main>
                </div>
                <AboutModal
                    open={aboutDialogOpen}
                    onClose={handleAboutDialogCancel}
                    maxWidth="sm"
                />
                <AddTermsDialog
                    open={addTermsFormOpen}
                    onClose={() => setAddTermsFormOpen(false)}
                />
                <AddDefaultAvatarDialog
                    open={addDefaultAvatarFormOpen}
                    onClose={() => setAddDefaultAvatarFormOpen(false)}
                />
            </>
        )
    );
}

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
    },
    toolbar: {
        paddingRight: 24,
        height: 64,
    },
    toolbarIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 8px',
        ...theme.mixins.toolbar,
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    },
    menuButton: {
        marginRight: 36,
    },
    menuButtonHidden: {
        display: 'none',
    },
    title: {
        flexGrow: 1,
        textAlign: 'center',
        fontFamily: 'Poppins',
        fontWeight: 500,
        fontSize: '24px',
    },
    drawerPaper: {
        backgroundColor: theme.palette.secondary.main,
        position: 'relative',
        whiteSpace: 'nowrap',
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    drawerPaperClose: {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
    },
    appBarSpacer: theme.mixins.toolbar,
    content: {
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
        background: theme.palette.primary.lighter,
    },
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
    },
    logo: {
        color: '#FFFFFF',
        height: 40,
        width: 'auto',
    },
    accountIcon: {
        color: '#F8FBFF',
        '&:hover': {
            color: '#FFFFFF',
            backgroundColor: 'transparent',
        },
    },
    icon: {
        fontSize: 32,
        color: theme.palette.primary.lighter,
        marginLeft: 10,
    },
}));
